import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setLocation } from "../../../../redux/locationReducer";
import { trackEvent } from "../../../../hooks/useGTM";
import {
  getCountries,
  getDepartments,
  getProvinces,
  getDistricts,
} from "../../../../lib/locationApis";

import styles from "./LocationSelector.module.css";

// Define types to fix TypeScript errors
interface DistrictType {
  id: string;
  distrito: string;
  opsLeadTime: number | null;
  ubigeoCode: string;
  // New zone arrays for array-based delivery zones
  deliveryFeeZones: { documentId: string; name: string }[];
  deliveryLeadTimeZones: { documentId: string; name: string }[];
  // Legacy compatibility - keep single zone fields for backward compatibility
  deliveryFeeZone: { documentId: string; name: string } | null;
  deliveryLeadTimeZone: { documentId: string; name: string } | null;
}

interface LocationState {
  country: string;
  countryThumbnail: string;
  department: string;
  province: string;
  district: string;
  opsLeadTime: number | null;
  ubigeoCode: string;
  address: string;
  phoneNumber: string;
  name: string;
  lastname: string;
  // New zone arrays for array-based delivery zones
  deliveryFeeZones: { documentId: string; name: string }[];
  deliveryLeadTimeZones: { documentId: string; name: string }[];
  // Legacy compatibility - keep single zone fields for backward compatibility
  deliveryFeeZone: { documentId: string; name: string } | null;
  deliveryLeadTimeZone: { documentId: string; name: string } | null;
}

interface LocationSelectorProps {
  onClose: () => void;
  initialLocation?: {
    name?: string;
    lastname?: string;
    country?: string;
    department?: string;
    province?: string;
    district?: string;
    address?: string;
    phoneNumber?: string;
    // New zone arrays for array-based delivery zones
    deliveryFeeZones?: { documentId: string; name: string }[];
    deliveryLeadTimeZones?: { documentId: string; name: string }[];
    // Legacy compatibility
    deliveryFeeZone?: { documentId: string; name: string } | null;
  };
}

const LocationSelector = ({
  onClose,
  initialLocation,
}: LocationSelectorProps) => {
  const dispatch = useDispatch();
  const locationFromRedux = useSelector(
    (state: any) => state.location
  ) as LocationState;

  // Prevent duplicate data loading
  const initialLoadComplete = useRef(false);

  // Add loading state trackers for each dropdown
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);

  // Store location data selections (controlled inputs)
  const [selectedCountry, setSelectedCountry] = useState(
    locationFromRedux.country || "Perú"
  );
  const [selectedDepartment, setSelectedDepartment] = useState(
    locationFromRedux.department || ""
  );
  const [selectedProvince, setSelectedProvince] = useState(
    locationFromRedux.province || ""
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    locationFromRedux.district || ""
  );

  // Store fetched location data options
  const [countries, setCountries] = useState<
    { id: string; name: string; thumbnail: { url: string } }[]
  >([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [districts, setDistricts] = useState<DistrictType[]>([]);

  // Store personal info
  const [address, setAddress] = useState(locationFromRedux.address || "");
  const [phoneNumber, setPhoneNumber] = useState(
    locationFromRedux.phoneNumber || ""
  );
  const [name, setName] = useState(locationFromRedux.name || "");
  const [lastname, setLastname] = useState(locationFromRedux.lastname || "");

  // Add form validation state
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track data load requests to prevent duplicate requests
  const pendingRequests = useRef({
    countries: false,
    departments: false,
    provinces: false,
    districts: false,
  });

  // Enhanced location initialization logic - run only once
  useEffect(() => {
    if (initialLoadComplete.current) return;

    console.log("Initializing location data - first load only");

    // Always prefer Redux state for consistency, but allow prop override for specific fields
    setSelectedCountry(
      locationFromRedux.country || initialLocation?.country || "Perú"
    );
    setSelectedDepartment(
      locationFromRedux.department || initialLocation?.department || ""
    );
    setSelectedProvince(
      locationFromRedux.province || initialLocation?.province || ""
    );
    setSelectedDistrict(
      locationFromRedux.district || initialLocation?.district || ""
    );
    setAddress(locationFromRedux.address || initialLocation?.address || "");
    setPhoneNumber(
      locationFromRedux.phoneNumber || initialLocation?.phoneNumber || ""
    );
    setName(locationFromRedux.name || initialLocation?.name || "");
    setLastname(locationFromRedux.lastname || initialLocation?.lastname || "");

    initialLoadComplete.current = true;
  }, [locationFromRedux, initialLocation]);

  // Memoize the loadDistricts function for better performance
  const loadDistricts = useCallback(
    (department: string, province: string) => {
      if (!department || !province) return;

      // Skip if already loading the same data
      if (pendingRequests.current.districts) {
        console.log("Skipping duplicate districts request");
        return;
      }

      pendingRequests.current.districts = true;
      setIsLoadingDistricts(true);
      console.log("Fetching districts for:", department, province);

      getDistricts(department, province)
        .then((data) => {
          console.log("Fetched districts:", data.length);
          setDistricts(data);
          setIsLoadingDistricts(false);
          pendingRequests.current.districts = false;

          if (
            locationFromRedux.district &&
            locationFromRedux.province === province
          ) {
            const matchingDistrict = data.find(
              (d: DistrictType) => d.distrito === locationFromRedux.district
            );
            if (matchingDistrict) {
              setSelectedDistrict(matchingDistrict.distrito);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching districts:", error);
          setDistricts([]);
          setIsLoadingDistricts(false);
          pendingRequests.current.districts = false;
        });
    },
    [locationFromRedux.district, locationFromRedux.province]
  );

  // Now define loadProvinces with loadDistricts as a dependency
  const loadProvinces = useCallback(
    (department: string) => {
      if (!department) return;

      // Skip if already loading the same data
      if (pendingRequests.current.provinces) {
        console.log("Skipping duplicate provinces request");
        return;
      }

      pendingRequests.current.provinces = true;
      setIsLoadingProvinces(true);
      getProvinces(department)
        .then((provinces) => {
          console.log("Fetched provinces:", provinces.length);
          setProvinces(provinces);
          setIsLoadingProvinces(false);
          pendingRequests.current.provinces = false;

          if (
            locationFromRedux.province &&
            department === locationFromRedux.department
          ) {
            // Only load districts if we have a province selected and it matches the loaded department
            loadDistricts(department, locationFromRedux.province);
          }
        })
        .catch((error) => {
          console.error("Error fetching provinces:", error);
          setProvinces([]);
          setIsLoadingProvinces(false);
          pendingRequests.current.provinces = false;
        });
    },
    [locationFromRedux.province, locationFromRedux.department, loadDistricts]
  );

  // Initial load for countries and departments - run only once
  useEffect(() => {
    // Skip if already completed
    if (countries.length > 0 && departments.length > 0) return;

    // Countries
    if (!pendingRequests.current.countries) {
      pendingRequests.current.countries = true;
      setIsLoadingCountries(true);
      getCountries()
        .then((data) => {
          console.log("Fetched countries:", data.length);
          setCountries(data);
          setIsLoadingCountries(false);
          pendingRequests.current.countries = false;
        })
        .catch((error) => {
          console.error("Error fetching countries:", error);
          setCountries([]);
          setIsLoadingCountries(false);
          pendingRequests.current.countries = false;
        });
    }

    // Departments
    if (!pendingRequests.current.departments) {
      pendingRequests.current.departments = true;
      setIsLoadingDepartments(true);
      getDepartments()
        .then((data) => {
          console.log("Fetched departments:", data.length);
          setDepartments(data);
          setIsLoadingDepartments(false);
          pendingRequests.current.departments = false;

          // After loading departments, check if we need to load provinces too
          if (locationFromRedux.department) {
            loadProvinces(locationFromRedux.department);
          }
        })
        .catch((error) => {
          console.error("Error fetching departments:", error);
          setDepartments([]);
          setIsLoadingDepartments(false);
          pendingRequests.current.departments = false;
        });
    }
  }, [
    countries.length,
    departments.length,
    locationFromRedux.department,
    loadProvinces,
  ]);

  // Handle selected department change
  useEffect(() => {
    if (selectedDepartment && departments.length > 0) {
      const isNewSelection =
        selectedDepartment !== locationFromRedux.department;

      // If department changed, reset child selections
      if (isNewSelection) {
        setSelectedProvince("");
        setSelectedDistrict("");
      }

      // Load provinces for the selected department
      loadProvinces(selectedDepartment);
    }
  }, [
    selectedDepartment,
    departments.length,
    locationFromRedux.department,
    loadProvinces,
  ]);

  // Handle province selection change
  useEffect(() => {
    if (selectedDepartment && selectedProvince && provinces.length > 0) {
      const isNewSelection = selectedProvince !== locationFromRedux.province;

      // If province changed, reset district
      if (isNewSelection) {
        setSelectedDistrict("");
      }

      // Load districts for the selected department and province
      loadDistricts(selectedDepartment, selectedProvince);
    }
  }, [
    selectedProvince,
    selectedDepartment,
    provinces.length,
    locationFromRedux.province,
    loadDistricts,
  ]);

  // Focus first input on desktop
  useEffect(() => {
    if (window.innerWidth > 480) {
      const input = document.querySelector("input");
      if (input) input.focus();
    }
  }, []);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const errors: { [key: string]: string } = {};

    if (!selectedCountry) errors.country = "Por favor, selecciona un país";
    if (!selectedDepartment)
      errors.department = "Por favor, selecciona un departamento";
    if (!selectedProvince)
      errors.province = "Por favor, selecciona una provincia";
    if (!selectedDistrict)
      errors.district = "Por favor, selecciona un distrito";
    if (!address.trim()) errors.address = "La dirección es requerida";
    if (!name.trim()) errors.name = "El nombre es requerido";
    if (!lastname.trim()) errors.lastname = "El apellido es requerido";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    selectedCountry,
    selectedDepartment,
    selectedProvince,
    selectedDistrict,
    address,
    name,
    lastname,
  ]);

  // Handle saving location
  const handleSaveLocation = useCallback(() => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    const selectedCountryData = countries.find(
      (country) => country.name === selectedCountry
    );

    const fallbackDistrictData: DistrictType = {
      id: "",
      distrito: "",
      opsLeadTime: null,
      // New zone arrays for array-based delivery zones
      deliveryFeeZones: [],
      deliveryLeadTimeZones: [],
      // Legacy compatibility
      deliveryFeeZone: null,
      deliveryLeadTimeZone: null,
      ubigeoCode: "",
    };

    const selectedDistrictData =
      districts.find((district) => district.distrito === selectedDistrict) ||
      fallbackDistrictData;

    const selectedLocation = {
      country: selectedCountry,
      department: selectedDepartment,
      province: selectedProvince,
      district: selectedDistrict,
      countryThumbnail: selectedCountryData?.thumbnail.url || "",
      opsLeadTime: selectedDistrictData.opsLeadTime ?? 0,
      ubigeoCode: selectedDistrictData.ubigeoCode,
      address,
      phoneNumber,
      name,
      lastname,
      // New zone arrays for array-based delivery zones
      deliveryFeeZones: selectedDistrictData.deliveryFeeZones || [],
      deliveryLeadTimeZones: selectedDistrictData.deliveryLeadTimeZones || [],
      // Legacy compatibility
      deliveryFeeZone: selectedDistrictData.deliveryFeeZone,
      deliveryLeadTimeZone: selectedDistrictData.deliveryLeadTimeZone,
    };

    // Zone validation before saving
    const zoneValidationErrors: string[] = [];
    if (
      !selectedDistrictData.deliveryFeeZones ||
      selectedDistrictData.deliveryFeeZones.length === 0
    ) {
      zoneValidationErrors.push(
        "Este distrito no tiene zonas de costo de envío configuradas"
      );
    }
    if (
      !selectedDistrictData.deliveryLeadTimeZones ||
      selectedDistrictData.deliveryLeadTimeZones.length === 0
    ) {
      zoneValidationErrors.push(
        "Este distrito no tiene zonas de tiempo de entrega configuradas"
      );
    }

    if (zoneValidationErrors.length > 0) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        district: `Zona no disponible: ${zoneValidationErrors.join(". ")}`,
      }));
      setIsSubmitting(false);
      return;
    }

    console.log("Saving location to Redux:", selectedLocation);

    dispatch(setLocation(selectedLocation));

    // Track location saved event
    trackEvent("location_saved", {
      country: selectedLocation.country,
      department: selectedLocation.department,
      province: selectedLocation.province,
      district: selectedLocation.district,
      has_delivery_zone: !!selectedLocation.deliveryFeeZone,
    });

    console.log(
      "Location data dispatched to Redux - useLocationSync will handle cart group updates"
    );

    setIsSubmitting(false);
    onClose();
  }, [
    isSubmitting,
    validateForm,
    countries,
    selectedCountry,
    districts,
    selectedDistrict,
    selectedDepartment,
    selectedProvince,
    address,
    phoneNumber,
    name,
    lastname,
    dispatch,
    onClose,
  ]);

  // Memoize the rendered select options to prevent unnecessary re-renders
  const countryOptions = useMemo(
    () =>
      countries.map((country) => (
        <option key={country.id} value={country.name}>
          {country.name}
        </option>
      )),
    [countries]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((department) => (
        <option key={department} value={department}>
          {department}
        </option>
      )),
    [departments]
  );

  const provinceOptions = useMemo(
    () =>
      provinces.map((province) => (
        <option key={province} value={province}>
          {province}
        </option>
      )),
    [provinces]
  );

  const districtOptions = useMemo(
    () =>
      districts.length > 0 ? (
        districts.map((district) => (
          <option key={district.id} value={district.distrito}>
            {district.distrito}
          </option>
        ))
      ) : (
        <option value="" disabled>
          No hay distritos disponibles
        </option>
      ),
    [districts]
  );

  return (
    <div className={styles.container}>
      <div className={styles.selectContainer}>
        <div className={styles.row}>
          <label className={styles.label}>País</label>
          <select
            className={`${styles.select} ${
              formErrors.country ? styles.error : ""
            } ${isLoadingCountries ? styles.loading : ""}`}
            value={selectedCountry || ""}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setFormErrors({ ...formErrors, country: "" });
            }}
            disabled={isLoadingCountries}
          >
            <option value="" disabled>
              {isLoadingCountries ? "Cargando países..." : "País"}
            </option>
            {countryOptions}
          </select>
          {formErrors.country && (
            <p className={styles.errorText}>{formErrors.country}</p>
          )}
          {isLoadingCountries && (
            <div className={styles.loadingIndicator}></div>
          )}
        </div>

        <div className={styles.row}>
          <label className={styles.label}>Departamento</label>
          <select
            className={`${styles.select} ${
              formErrors.department ? styles.error : ""
            } ${isLoadingDepartments ? styles.loading : ""}`}
            value={selectedDepartment || ""}
            onChange={(e) => {
              setSelectedDepartment(e.target.value);
              setFormErrors({ ...formErrors, department: "" });
            }}
            disabled={!selectedCountry || isLoadingDepartments}
          >
            <option value="" disabled>
              {isLoadingDepartments
                ? "Cargando departamentos..."
                : "Departamento"}
            </option>
            {departmentOptions}
          </select>
          {formErrors.department && (
            <p className={styles.errorText}>{formErrors.department}</p>
          )}
          {isLoadingDepartments && (
            <div className={styles.loadingIndicator}></div>
          )}
        </div>

        <div className={styles.row}>
          <label className={styles.label}>Provincia</label>
          <select
            className={`${styles.select} ${
              formErrors.province ? styles.error : ""
            } ${isLoadingProvinces ? styles.loading : ""}`}
            value={selectedProvince || ""}
            onChange={(e) => {
              setSelectedProvince(e.target.value);
              setFormErrors({ ...formErrors, province: "" });
            }}
            disabled={!selectedDepartment || isLoadingProvinces}
          >
            <option value="" disabled>
              {isLoadingProvinces ? "Cargando provincias..." : "Provincia"}
            </option>
            {provinceOptions}
          </select>
          {formErrors.province && (
            <p className={styles.errorText}>{formErrors.province}</p>
          )}
          {isLoadingProvinces && (
            <div className={styles.loadingIndicator}></div>
          )}
        </div>

        <div className={styles.row}>
          <label className={styles.label}>Distrito</label>
          <select
            className={`${styles.select} ${
              formErrors.district ? styles.error : ""
            } ${isLoadingDistricts ? styles.loading : ""}`}
            value={selectedDistrict || ""}
            onChange={(e) => {
              setSelectedDistrict(e.target.value);
              setFormErrors({ ...formErrors, district: "" });
            }}
            disabled={!selectedProvince || isLoadingDistricts}
          >
            <option value="" disabled>
              {isLoadingDistricts ? "Cargando distritos..." : "Distrito"}
            </option>
            {districtOptions}
          </select>
          {formErrors.district && (
            <p className={styles.errorText}>{formErrors.district}</p>
          )}
          {isLoadingDistricts && (
            <div className={styles.loadingIndicator}></div>
          )}
        </div>

        {/* Zone information will be validated during save instead of displayed */}

        <div className={styles.rowFlex}>
          <div className={styles.flexChild}>
            <label className={styles.label}>Nombre</label>
            <input
              className={`${styles.input} ${
                formErrors.name ? styles.error : ""
              }`}
              type="text"
              value={name}
              placeholder="Nombre"
              onChange={(e) => {
                setName(e.target.value);
                setFormErrors({ ...formErrors, name: "" });
              }}
            />
            {formErrors.name && (
              <p className={styles.errorText}>{formErrors.name}</p>
            )}
          </div>
          <div className={styles.flexChild}>
            <label className={styles.label}>Apellido</label>
            <input
              className={`${styles.input} ${
                formErrors.lastname ? styles.error : ""
              }`}
              type="text"
              value={lastname}
              placeholder="Apellido"
              onChange={(e) => {
                setLastname(e.target.value);
                setFormErrors({ ...formErrors, lastname: "" });
              }}
            />
            {formErrors.lastname && (
              <p className={styles.errorText}>{formErrors.lastname}</p>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <label className={styles.label}>Dirección</label>
          <input
            className={`${styles.input} ${
              formErrors.address ? styles.error : ""
            }`}
            value={address}
            placeholder="Dirección"
            onChange={(e) => {
              setAddress(e.target.value);
              setFormErrors({ ...formErrors, address: "" });
            }}
          />
          {formErrors.address && (
            <p className={styles.errorText}>{formErrors.address}</p>
          )}
        </div>

        <div className={styles.row}>
          <label className={styles.label}>Número de teléfono</label>
          <input
            className={styles.input}
            value={phoneNumber}
            placeholder="Número de teléfono"
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.buttonContainer}>
        <button
          className={`buttonMain ${isSubmitting ? styles.submitting : ""}`}
          onClick={handleSaveLocation}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Guardando..." : "Listo"}
        </button>
      </div>
    </div>
  );
};

export default LocationSelector;
